import React from 'react';
import LyricsRecordPreview, { getLyricsText } from './LyricsRecordPreview';
import {
  canDownloadRecord,
  canViewRecord,
  getAbsoluteUrl,
  getPreviewImageUrl,
  getStatusColor,
  isAudioRecord,
  isLyricsRecord,
  isVideoRecord,
} from './creationRecordUtils';

export default function RecordCard({
  record,
  t,
  typeLabel,
  onDelete,
  onDownload,
  onPlayVideo,
  onShare,
  onView,
}) {
  return (
    <div className="record-card">
      <RecordCardHeader record={record} t={t} onShare={onShare} />
      <RecordPreview record={record} t={t} onPlayVideo={onPlayVideo} onView={onView} />
      <RecordCardBody typeLabel={typeLabel} />
      <RecordCardFooter record={record} t={t} onDelete={onDelete} onDownload={onDownload} onView={onView} />
    </div>
  );
}

function RecordCardHeader({ record, t, onShare }) {
  return (
    <div className="card-header">
      <span className={`status ${getStatusColor(record.status)}`}>{record.status}</span>
      <div className="header-right">
        <span className="date">{record.date}</span>
        {record.status === 'COMPLETED' && <ShareButton record={record} t={t} onShare={onShare} />}
      </div>
    </div>
  );
}

function ShareButton({ record, t, onShare }) {
  return (
    <button className="btn-share" onClick={() => onShare(record)}>
      <img src="/icons/share.svg" alt={t('creationRecord.share')} />
    </button>
  );
}

function RecordCardBody({ typeLabel }) {
  return (
    <div className="card-body">
      <div className="type-label">
        <span className="type-dot"></span>
        <span className="type-text">{typeLabel}</span>
      </div>
      <div className="info"></div>
    </div>
  );
}

function RecordCardFooter({ record, t, onDelete, onDownload, onView }) {
  return (
    <div className="card-footer">
      <button className="btn-view" onClick={() => onView(record)} disabled={record.status === 'FAILED' || !canViewRecord(record)}>
        <img src="/icons/view.svg" alt={t('creationRecord.view')} />
      </button>
      <button className="btn-download" onClick={() => onDownload(record)} disabled={!canDownloadRecord(record)}>
        <img src="/icons/download.svg" alt={t('creationRecord.download')} />
      </button>
      <button className="btn-delete" onClick={() => onDelete(record.id)}>
        <img src="/icons/delete.svg" alt={t('creationRecord.delete')} />
      </button>
    </div>
  );
}

function RecordPreview({ record, t, onPlayVideo, onView }) {
  return (
    <div className="card-image">
      {previewContent(record, t)}
      {isVideoRecord(record) && <PlayOverlay onClick={() => onPlayVideo(record)} />}
      {isAudioRecord(record) && <PlayOverlay onClick={() => onView(record)} />}
      {!isLyricsRecord(record) && !isVideoRecord(record) && !isAudioRecord(record) && (
        <div className="no-image" style={{ display: getAbsoluteUrl(record.thumbnail || record.imageUrl) ? 'none' : 'flex' }}>
          <span>{t('creationRecord.noPreview')}</span>
        </div>
      )}
    </div>
  );
}

function previewContent(record, t) {
  if (isLyricsRecord(record)) {
    return <LyricsRecordPreview text={getLyricsText(record)} emptyText={t('creationRecord.noPreview')} />;
  }
  if (isVideoRecord(record)) return mediaCover(record, t, 'video');
  if (isAudioRecord(record)) return mediaCover(record, t, 'audio');
  return imageCover(record, t);
}

function mediaCover(record, t, kind) {
  const preview = getPreviewImageUrl(record);
  if (preview) {
    return <img src={getAbsoluteUrl(preview)} alt={t('creationRecord.creationThumbnail')} />;
  }
  return kind === 'video' ? <DefaultVideoCover /> : <DefaultAudioCover />;
}

function imageCover(record, t) {
  const src = record.thumbnail || record.imageUrl;
  if (!src) return null;
  return (
    <img
      src={getAbsoluteUrl(src)}
      alt={t('creationRecord.creationThumbnail')}
      onError={(event) => {
        event.target.style.display = 'none';
        event.target.nextSibling.style.display = 'flex';
      }}
    />
  );
}

function PlayOverlay({ onClick }) {
  return (
    <div className="record-play-overlay">
      <button className="record-play-button" onClick={(event) => { event.stopPropagation(); onClick(); }}>
        <svg width="26" height="26" viewBox="0 0 24 24"><path d="M8 5l11 7-11 7z" fill="#ffffff" /></svg>
      </button>
    </div>
  );
}

function DefaultVideoCover() {
  return (
    <div className="default-video-cover">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
        <line x1="7" y1="2" x2="7" y2="22"></line>
        <line x1="17" y1="2" x2="17" y2="22"></line>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <line x1="2" y1="7" x2="7" y2="7"></line>
        <line x1="2" y1="17" x2="7" y2="17"></line>
        <line x1="17" y1="17" x2="22" y2="17"></line>
        <line x1="17" y1="7" x2="22" y2="7"></line>
      </svg>
    </div>
  );
}

function DefaultAudioCover() {
  return (
    <div className="default-audio-cover">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    </div>
  );
}
